import { API_BASE_URL, APP_TYPE } from '@soundbyte/config/constants';
import type { ApiResponse, ApiError } from '@soundbyte/types';

// Storage key for session token (Safari/iOS fallback)
const SESSION_TOKEN_KEY = `soundbyte-session-token-${APP_TYPE}`;

/**
 * API Client - Base class for all API requests
 *
 * Uses session-based authentication with cookies (primary).
 * Falls back to bearer token auth for Safari/iOS where cookies are blocked.
 * Sends X-App-Type header to identify the app for session isolation.
 */
class ApiClient {
  private baseUrl: string;
  private onUnauthorized: (() => void) | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Register a callback to be called when a 401 Unauthorized response is received.
   * Used by the auth store to clear session state and redirect to login.
   */
  setOnUnauthorized(callback: () => void) {
    this.onUnauthorized = callback;
  }

  /**
   * Store session token for Safari/iOS fallback
   */
  setSessionToken(token: string | null) {
    if (token) {
      try {
        localStorage.setItem(SESSION_TOKEN_KEY, token);
      } catch {
        // localStorage may not be available
      }
    } else {
      try {
        localStorage.removeItem(SESSION_TOKEN_KEY);
      } catch {
        // Ignore
      }
    }
  }

  /**
   * Get stored session token
   */
  getSessionToken(): string | null {
    try {
      return localStorage.getItem(SESSION_TOKEN_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Clear session token (on logout)
   */
  clearSessionToken() {
    this.setSessionToken(null);
  }

  /**
   * Build headers for requests
   * Includes X-App-Type header for session isolation between admin and playground
   * Includes Authorization header with bearer token for Safari/iOS fallback
   */
  private getHeaders(customHeaders?: Record<string, string>): Headers {
    const headers = new Headers({
      'Content-Type': 'application/json',
      'X-App-Type': APP_TYPE, // Identifies app for session isolation
      ...customHeaders,
    });

    // Add bearer token for Safari/iOS fallback
    const token = this.getSessionToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * Handle API response
   */
  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    // Handle 304 Not Modified BEFORE the !response.ok check,
    // since 304 is not in the 200-299 range but is NOT an error.
    // 304 responses have no body — return success with null data so callers
    // can fall back to their stored/cached data.
    if (response.status === 304) {
      return { data: null as T, error: null, success: true };
    }

    if (!response.ok) {
      const error: ApiError = {
        code: `HTTP_${response.status}`,
        message: response.statusText,
      };

      try {
        const errorBody = await response.json();
        // Backend sends: { success: false, error: { message, code, details } }
        if (errorBody.error && typeof errorBody.error === 'object') {
          error.code = errorBody.error.code || error.code;
          error.message = errorBody.error.message || error.message;
          error.details = errorBody.error.details;
        } else {
          // Fallback for flat error format
          error.code = errorBody.code || error.code;
          error.message = errorBody.message || error.message;
          error.details = errorBody.details;
        }
      } catch {
        // Response body is not JSON
      }

      // Session expired — clear auth state so UI redirects to login
      if (response.status === 401 && this.onUnauthorized) {
        this.onUnauthorized();
      }

      return { data: null, error, success: false };
    }

    try {
      const data = await response.json();
      // Backend returns { success, data, error, meta } format
      if (data.success !== undefined) {
        return { data: data.data, error: data.error, success: data.success, meta: data.meta };
      }
      return { data, error: null, success: true };
    } catch {
      // Empty response body
      return { data: null as T, error: null, success: true };
    }
  }

  /**
   * GET request
   */
  async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>
  ): Promise<ApiResponse<T>> {
    const url = new URL(`${this.baseUrl}${endpoint}`);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include', // Include session cookies
        cache: 'no-store', // Prevent 304 responses — API calls need fresh data
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        data: null,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
        success: false,
      };
    }
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    body?: unknown
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include', // Include session cookies
        body: body ? JSON.stringify(body) : undefined,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        data: null,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
        success: false,
      };
    }
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    body?: unknown
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        credentials: 'include', // Include session cookies
        body: body ? JSON.stringify(body) : undefined,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        data: null,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
        success: false,
      };
    }
  }

  /**
   * PATCH request
   */
  async patch<T>(
    endpoint: string,
    body?: unknown
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        credentials: 'include', // Include session cookies
        body: body ? JSON.stringify(body) : undefined,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        data: null,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
        success: false,
      };
    }
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
        credentials: 'include', // Include session cookies
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        data: null,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
        success: false,
      };
    }
  }

  /**
   * Upload file with multipart form data.
   *
   * Defaults to POST for backward compatibility. Pass `options.method` to
   * issue a PATCH (e.g. updating an avatar via PATCH /users/me/avatar).
   */
  async upload<T>(
    endpoint: string,
    file: File,
    fieldName: string = 'file',
    additionalData?: Record<string, string>,
    options?: { method?: 'POST' | 'PATCH' | 'PUT' }
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append(fieldName, file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    try {
      // Don't set Content-Type for multipart - browser will set it with boundary
      const headers: Record<string, string> = {
        'X-App-Type': APP_TYPE, // Identifies app for session isolation
      };

      // Add bearer token for Safari/iOS fallback
      const token = this.getSessionToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: options?.method || 'POST',
        headers,
        credentials: 'include', // Include session cookies
        body: formData,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        data: null,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
        success: false,
      };
    }
  }

  /**
   * Upload multiple files with multipart form data
   */
  async uploadMultiple<T>(
    endpoint: string,
    files: File[],
    fieldName: string = 'files',
    additionalData?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append(fieldName, file);
    });

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    try {
      // Don't set Content-Type for multipart - browser will set it with boundary
      const headers: Record<string, string> = {
        'X-App-Type': APP_TYPE, // Identifies app for session isolation
      };

      // Add bearer token for Safari/iOS fallback
      const token = this.getSessionToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers,
        credentials: 'include', // Include session cookies
        body: formData,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        data: null,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
        success: false,
      };
    }
  }
}

// Export singleton instance
export const api = new ApiClient();

export default ApiClient;
